import React, { useEffect, useRef, useState } from "react";
import { FiCalendar, FiChevronLeft, FiChevronRight } from "react-icons/fi";

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
const WEEKDAY_LABELS = ["S", "M", "T", "W", "T", "F", "S"];

const toISODate = (year, month, day) =>
  `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;

const formatDisplayDate = (isoDate) => {
  if (!isoDate) return "";
  const [year, month, day] = isoDate.split("-").map(Number);
  return `${String(day).padStart(2, "0")}/${String(month).padStart(2, "0")}/${year}`;
};

const CustomDatePicker = ({ id, value, onChange, required, max }) => {
  const [isOpen, setIsOpen] = useState(false);
  const today = new Date();
  const maxDate = max ? new Date(`${max}T00:00:00`) : today;
  const minYear = maxDate.getFullYear() - 100;
  const yearOptions = [];
  for (let yr = maxDate.getFullYear(); yr >= minYear; yr--) yearOptions.push(yr);

  const initialDate = value ? new Date(`${value}T00:00:00`) : today;
  const [viewYear, setViewYear] = useState(initialDate.getFullYear());
  const [viewMonth, setViewMonth] = useState(initialDate.getMonth());

  const containerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const firstWeekday = new Date(viewYear, viewMonth, 1).getDay();

  const isFutureMonth =
    viewYear > maxDate.getFullYear() ||
    (viewYear === maxDate.getFullYear() && viewMonth >= maxDate.getMonth());

  const maxMonthForView = viewYear === maxDate.getFullYear() ? maxDate.getMonth() : 11;

  const handleYearChange = (yr) => {
    setViewYear(yr);
    if (yr === maxDate.getFullYear() && viewMonth > maxDate.getMonth()) {
      setViewMonth(maxDate.getMonth());
    }
  };

  const goToPrevMonth = () => {
    setViewMonth((m) => {
      if (m === 0) { setViewYear((y) => y - 1); return 11; }
      return m - 1;
    });
  };

  const goToNextMonth = () => {
    if (isFutureMonth) return;
    setViewMonth((m) => {
      if (m === 11) { setViewYear((y) => y + 1); return 0; }
      return m + 1;
    });
  };

  const handleSelectDay = (day) => {
    const iso = toISODate(viewYear, viewMonth, day);
    onChange(iso);
    setIsOpen(false);
  };

  const isDayDisabled = (day) => {
    const candidate = new Date(viewYear, viewMonth, day);
    candidate.setHours(0, 0, 0, 0);
    const cap = new Date(maxDate);
    cap.setHours(0, 0, 0, 0);
    return candidate > cap;
  };

  const isToday = (day) =>
    day === today.getDate() &&
    viewMonth === today.getMonth() &&
    viewYear === today.getFullYear();

  const isSelected = (day) =>
    value === toISODate(viewYear, viewMonth, day);

  const cells = [];
  for (let i = 0; i < firstWeekday; i++) cells.push(null);
  for (let day = 1; day <= daysInMonth; day++) cells.push(day);

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        id={id}
        onClick={() => setIsOpen((o) => !o)}
        className={`w-full h-10 sm:h-11 text-xs sm:text-sm px-3 py-2 border border-[#FFFFFF80] bg-white rounded-lg text-left flex items-center justify-between focus:outline-none focus:ring-2 focus:ring-white focus:ring-opacity-30 ${
          value ? "text-black" : "text-[#9CA3AF]"
        }`}
      >
        <span className="text-gray-400">{value ? formatDisplayDate(value) : "Select date"}</span>
        <FiCalendar className="text-[#ef5b5b] shrink-0" size={16} />
      </button>

      {required && (
        <input
          type="text"
          value={value}
          required
          readOnly
          tabIndex={-1}
          className="absolute inset-0 opacity-0 pointer-events-none h-0 w-0"
          aria-hidden="true"
        />
      )}

      {isOpen && (
        <div className="absolute z-20 mt-2 w-72 max-w-[85vw] bg-white text-black rounded-xl shadow-2xl border border-[#F3F4F6] p-3 animate-[fadeIn_0.12s_ease-out]">
          {/* Header */}
          <div className="flex items-center justify-between mb-3 px-1">
            <button
              type="button"
              onClick={goToPrevMonth}
              className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-[#ef5b5b]/10 text-[#ef5b5b] transition"
              aria-label="Previous month"
            >
              <FiChevronLeft size={18} />
            </button>
            <span className="flex items-center gap-1">
              <select
                value={viewMonth}
                onChange={(e) => setViewMonth(Number(e.target.value))}
                className="font-semibold text-sm bg-transparent border-none outline-none cursor-pointer hover:text-[#ef5b5b] transition appearance-none pr-0.5"
              >
                {MONTH_NAMES.map((name, idx) => (
                  <option key={name} value={idx} disabled={idx > maxMonthForView}>{name}</option>
                ))}
              </select>
              <select
                value={viewYear}
                onChange={(e) => handleYearChange(Number(e.target.value))}
                className="font-semibold text-sm bg-transparent border-none outline-none cursor-pointer hover:text-[#ef5b5b] transition appearance-none"
              >
                {yearOptions.map((yr) => (
                  <option key={yr} value={yr}>{yr}</option>
                ))}
              </select>
            </span>
            <button
              type="button"
              onClick={goToNextMonth}
              disabled={isFutureMonth}
              className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-[#ef5b5b]/10 text-[#ef5b5b] transition disabled:opacity-30 disabled:hover:bg-transparent disabled:cursor-not-allowed"
              aria-label="Next month"
            >
              <FiChevronRight size={18} />
            </button>
          </div>

          {/* Weekday labels */}
          <div className="grid grid-cols-7 gap-y-1 mb-1">
            {WEEKDAY_LABELS.map((wd, idx) => (
              <div key={idx} className="text-center text-[10px] font-medium text-[#9CA3AF]">
                {wd}
              </div>
            ))}
          </div>

          {/* Day cells */}
          <div className="grid grid-cols-7 gap-y-1">
            {cells.map((day, idx) => {
              if (day === null) return <div key={`empty-${idx}`} />;
              const disabled = isDayDisabled(day);
              const selected = isSelected(day);
              const todayFlag = isToday(day);
              return (
                <button
                  type="button"
                  key={day}
                  disabled={disabled}
                  onClick={() => handleSelectDay(day)}
                  className={[
                    "mx-auto w-8 h-8 flex items-center justify-center rounded-full text-xs transition",
                    disabled
                      ? "text-gray-300 cursor-not-allowed"
                      : "hover:bg-[#ef5b5b]/10 cursor-pointer",
                    selected ? "bg-[#ef5b5b] text-white font-semibold hover:bg-[#ef5b5b]" : "",
                    !selected && todayFlag ? "border border-[#ef5b5b] text-[#ef5b5b] font-semibold" : "",
                  ].join(" ")}
                >
                  {day}
                </button>
              );
            })}
          </div>

          {/* Footer */}
          <div className="flex justify-end mt-2 pt-2 border-t border-gray-100">
            <button
              type="button"
              onClick={() => {
                const iso = toISODate(today.getFullYear(), today.getMonth(), today.getDate());
                setViewYear(today.getFullYear());
                setViewMonth(today.getMonth());
                onChange(iso);
                setIsOpen(false);
              }}
              className="text-xs font-medium text-[#ef5b5b] hover:underline"
            >
              Today
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomDatePicker;
